import { AppSwitcher } from "components/AppSwitcher/AppSwitcher";
import ArchivedUser from "components/archived/ArchivedUser";
import { PageNotFound } from "components/design/PageNotFound";
import { ShareDocuments } from "components/documents/shareDocuments";
import { EstimatedDelivery } from "components/EstimatedDelivery/EstimatedDelivery";
import { GlobalContexts } from "components/GlobalContexts/GlobalContexts";
import { KeepInAppLinksInSameTab } from "components/KeepInAppLinksInSameTab/KeepInAppLinksInSameTab";
import { NotSupported } from "components/magicLink/notSupported/NotSupported";
import { Signup as V2Signup } from "v2/pages/Signup/Signup";
import { FilingsDetected } from "v2/pages/FilingsDetected/FilingsDetected";
import { FilingsDashboard } from "v2/pages/FilingsDashboard/FilingsDashboard";
import { MultiYearSetup } from "v2/pages/MultiYearSetup/MultiYearSetup";
import { PhoneVerify } from "components/magicLink/PhoneVerify";
import { PublicInvoiceView } from "components/PublicInvoice/PublicInvoiceView";
import { TIME_GAP_TO_SHOW_REMINDER } from "constants/routeState";
import { CurrentAppContextProvider } from "contexts/CurrentAppContext";
import { CurrentGroupContextProvider } from "contexts/CurrentGroupContext";
import { InvitationContext } from "contexts/InvitationContext";
import {
  isPracticeSignupInvite,
  isV1SignupInvite,
  SIGNUP_INVITE_PARAM,
} from "constants/signupInvite";
import { writeSignupInvite } from "utils/signupInviteStorage";
import { ContractorForm } from "ContractorForm";
import { ContractorFormSelector } from "ContractorFormSelector";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useGetEntitiesQuery } from "store/apis/group";
import { ConnectionConfirmation } from "pages/ConnectionConfirmation";
import { MailroomDashboard } from "pages/MailroomDashboard";
import { Payment3DSConfirmation } from "pages/Payment3DSConfirmation";
import { RedirectPage } from "pages/RedirectPage";
import { SettingsDashboard } from "pages/SettingsDashbaord";
import { parse } from "qs";
import { Suspense, useContext, useEffect, useState } from "react";
import {
  BrowserRouter,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { RestrictedRoute } from "RestrictedRoute";
import { RoleBasedRedirect } from "RoleBasedRedirect";
import { useGetUserEtdCountQuery } from "store/apis/profile";
import { MagicLink } from "./components/magicLink/magicLink";
import { VerifyOtpPage } from "./components/magicLink/verifyOtp";
import authContext, { AuthProvider } from "./jwt_context&axios/authContext";
import { ProtectedRoutes } from "./protectedRoutes";
import ForgotPassword from "./signInSignUp/forgotPassword";
import { Profile } from "./signInSignUp/profile";
import Loader from "components/DesignSystem/Loader/loader";
import { AdminDashboard } from "pages/AdminDashboard/AdminDashboard";
import { BooksDashboard } from "pages/Books/BooksDashboard/BooksDashboard";
import { FinancialSummaryExportPage } from "pages/Books/FinancialsSummary/FinancialSummaryExportPage";
import { CommunitygroupDashboard } from "pages/CommunitygroupDashboard/CommunityDashboard";
import PracticeDashboard from "pages/PracticeDashboard";
import { SalesTaxDashboard } from "pages/SalesTaxDashboard/SalesTaxDashboard";
import { TaxDashboard } from "pages/Tax/TaxDashboard";
import { PersonalTaxDashboard } from "pages/PersonalTax/PersonalTaxDashboard";
import {
  PERSONAL_TAX_BLOCKED_APP_ROUTES,
  PERSONAL_TAX_ROUTE,
} from "constants/personalTax";
import { PublicCalendar } from "pages/PublicCalendar/PublicCalendar";
import { PublicVendorBill } from "pages/PublicVendorBill/PublicVendorBill";
import { ApprovePublicBill } from "pages/PublicVendorBill/ApprovePublicBill";
import { IconPage } from "pages/IconPage";
import ColorGrid from "pages/Colors/Colors";
import SharedFilePreview from "pages/SharedFilePreview";
import { FilingDetails } from "components/fileTax/FilingDetails";
import TemplateMenu from "components/tasks/modal/AllTemplatesModal";
import { BOOKS } from "constants/taskCategoryType";
import AddTaskEntityModal from "components/tasks/modal/AddTaskEntityModal";
import { useUserOnboardingStepper } from "hooks/useUserOnboardingStepper";
import { Verifying } from "signInSignUp/Verifying";
import { GoogleRedirecting } from "pages/GoogleRedirecting";
import { MCPConfirmPage } from "pages/MCPConfirmPage";
import { Deadline } from "pages/Deadline/Deadline";
import { CompleteWithAI } from "v2/pages/CompleteWithAI/CompleteWithAI";
import { GlobalModals } from "components/GlobalModals/GlobalModals";

const AppRedirects = ({ from, to }: { from: string; to: string }) => {
  return (
    <Route
      path={from}
      render={(props) => (
        <Redirect
          to={`${to}${props.location.pathname}${props.location.search}`}
        />
      )}
    />
  );
};

const AdpTaxCreditsRoute = () => {
  const { search } = useLocation();
  const { groupId } = parse(search, { ignoreQueryPrefix: true });

  const adminUrl = groupId
    ? `/admin/crm/${groupId}/perks/adp-tax-credits`
    : "/admin/perkscrm?search=adp-tax-credits";

  return (
    <RoleBasedRedirect
      adminUrl={adminUrl}
      cpaUrl="/admin"
      customerUrl="/community/perks/adp-tax-credits"
    />
  );
};

const SignupRoute = () => {
  const { search } = useLocation();
  const invite = new URLSearchParams(search).get(SIGNUP_INVITE_PARAM);

  if (isV1SignupInvite(invite)) {
    writeSignupInvite(invite as string);

    if (isPracticeSignupInvite(invite)) {
      return <Redirect to="/setup?practice=true" />;
    }

    return <Redirect to="/setup" />;
  }

  return <V2Signup />;
};

const HomeRoute = () => {
  const { isCpa, isForeignCA, isCustomer, isPersonalTaxUser } =
    useRoleBasedView();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isMCPFlow = params.get("mcp_state") === "1";
  const nextUrl = params.get("next");
  const { data: { groups: [group] = [] } = {}, isLoading: isGroupLoading } =
    useGetEntitiesQuery(undefined, { skip: isCpa });

  if (isMCPFlow && nextUrl) {
    return <Redirect to={`/mcp-confirm?next=${encodeURIComponent(nextUrl)}`} />;
  }

  if (isForeignCA) {
    return <Redirect to="/practice" />;
  }

  if (isCpa) {
    return <Redirect to="/admin" />;
  }

  if (isPersonalTaxUser) {
    return <Redirect to={PERSONAL_TAX_ROUTE} />;
  }

  if (isCustomer && isGroupLoading) {
    return <Loader />;
  }

  if (
    isCustomer &&
    group?.is_pre_incorporate &&
    group?.has_published_incorporation_form
  ) {
    return <Redirect to="/tax/incorporation" />;
  }

  return <AppSwitcher />;
};

export const AllProtectedRoutes = () => {
  const {
    isCpa,
    isForeignCA,
    isUserTypeForeignCA,
    isCustomer,
    isPersonalTaxUser,
  } = useRoleBasedView();
  const estimatedDeliveryReminder = useModal();
  const { authtoken } = useContext(authContext);
  const reminderKey = `reminderTimestamp${authtoken.email}`;
  const { data } = useGetUserEtdCountQuery(void 0, {
    skip: !isCpa,
  });
  const { path } = useRouteMatch();

  useEffect(() => {
    const reminderTimestamp = localStorage.getItem(reminderKey);
    const currentTime = new Date().getTime();
    const reminderTimestampNumber = reminderTimestamp
      ? Number(reminderTimestamp)
      : null;

    if (
      data?.task_etd_pending_count &&
      data.task_etd_pending_count > 0 &&
      (!reminderTimestampNumber ||
        currentTime - reminderTimestampNumber > TIME_GAP_TO_SHOW_REMINDER)
    ) {
      estimatedDeliveryReminder.open();
      localStorage.setItem(reminderKey, currentTime.toString());
    }
  }, [data?.task_etd_pending_count]);

  const isFCA = isForeignCA || isUserTypeForeignCA;

  const renderTaxToPersonalTaxRedirect = ({
    match,
    location,
  }: RouteComponentProps<{ rest?: string }>) => (
    <Redirect
      to={`${PERSONAL_TAX_ROUTE}/${match.params.rest ?? ""}${location.search}`}
    />
  );

  return (
    <>
      {estimatedDeliveryReminder.isOpen && (
        <EstimatedDelivery
          pendingFilingCount={data!.task_etd_pending_count}
          {...estimatedDeliveryReminder}
        />
      )}
      <Switch>
        <RestrictedRoute path="/profile" exact component={Profile} />
        <RestrictedRoute path="/verify" exact component={Verifying} />
        <RestrictedRoute path="/archived" exact component={ArchivedUser} />
        <RestrictedRoute path="/deadline/:groupId" exact>
          <Deadline />
        </RestrictedRoute>

        <RestrictedRoute
          path="/payment-confirmation"
          component={Payment3DSConfirmation}
        />
        <RestrictedRoute
          path="/connection-confirmation/:bank"
          component={ConnectionConfirmation}
        />
        <RestrictedRoute path="/redirect" component={RedirectPage} />
        <RestrictedRoute exact path="/financial-summary-export">
          <FinancialSummaryExportPage />
        </RestrictedRoute>
        <GlobalContexts>
          <GlobalModals />
          <Suspense fallback={<Loader />}>
            <Switch>
              <RestrictedRoute
                path={`/filings/:taskId`}
                component={FilingDetails}
              />

              <RestrictedRoute exact path={`/tax/filings/addtask`}>
                <TemplateMenu to="/tax/filings" />
              </RestrictedRoute>
              <RestrictedRoute exact path={`/tax/filings/addtask/:templateId`}>
                <AddTaskEntityModal to="/tax/filings/addtask" />
              </RestrictedRoute>
              <RestrictedRoute
                path={`/tax/filings/:taskId`}
                component={FilingDetails}
              />

              {isPersonalTaxUser && (
                <Route
                  path="/tax/:rest*"
                  render={renderTaxToPersonalTaxRedirect}
                />
              )}
              {isPersonalTaxUser &&
                PERSONAL_TAX_BLOCKED_APP_ROUTES.map((route) => (
                  <Redirect key={route} from={route} to={PERSONAL_TAX_ROUTE} />
                ))}

              <RestrictedRoute exact path="/books/services/addtask">
                <TemplateMenu to="/books/services" taskCategoryType={BOOKS} />
              </RestrictedRoute>
              <RestrictedRoute exact path="/books/services/addtask/:templateId">
                <AddTaskEntityModal to="/books/services/addtask" />
              </RestrictedRoute>
              <RestrictedRoute exact path="/books/intragroup/services/addtask">
                <TemplateMenu
                  to="/books/intragroup/services"
                  taskCategoryType="INTRA_GROUP"
                />
              </RestrictedRoute>
              <RestrictedRoute
                exact
                path="/books/intragroup/services/addtask/:templateId"
              >
                <AddTaskEntityModal to="/books/intragroup/services/addtask" />
              </RestrictedRoute>
              <RestrictedRoute exact path="/books/services/:taskId">
                <FilingDetails
                  isFromServices
                  parentRoute="/books/services"
                  addEntityInUrl
                />
              </RestrictedRoute>
              <RestrictedRoute exact path="/books/intragroup/services/:taskId">
                <FilingDetails
                  isFromServices
                  parentRoute="/books/intragroup/services"
                  addEntityInUrl
                />
              </RestrictedRoute>
              <RestrictedRoute path="/books" component={BooksDashboard} />
              <RestrictedRoute path="/tax" component={TaxDashboard} />
              <RestrictedRoute
                path={PERSONAL_TAX_ROUTE}
                component={PersonalTaxDashboard}
              />
              <RestrictedRoute
                path="/redirecting"
                component={GoogleRedirecting}
              />

              <RestrictedRoute path="/mailroom" component={MailroomDashboard} />
              <RestrictedRoute exact path={`/sales-tax/services/addtask`}>
                <TemplateMenu
                  to={`/sales-tax/services`}
                  taskCategoryType="SALES_TAX"
                />
              </RestrictedRoute>
              <RestrictedRoute path={`/sales-tax/services/:taskId`} exact>
                <FilingDetails
                  isFromServices
                  parentRoute="/sales-tax/services"
                />
              </RestrictedRoute>
              <RestrictedRoute
                path="/sales-tax"
                component={SalesTaxDashboard}
              />

              <RestrictedRoute
                path="/community"
                render={() =>
                  isCpa ? <PageNotFound /> : <CommunitygroupDashboard />
                }
              />

              <RestrictedRoute
                path="/admin"
                render={(props) => {
                  const isAdminDocumentsRoute = props.location.pathname.match(
                    /\/admin\/crm\/(.*)\/documents\/(.*)/
                  );

                  const customerRedirectRoute =
                    isAdminDocumentsRoute && isAdminDocumentsRoute?.length > 0
                      ? `/tax/documents/${isAdminDocumentsRoute[2]}`
                      : `/`;

                  return isCpa ? (
                    <AdminDashboard />
                  ) : (
                    <Redirect
                      to={isFCA ? "/practice" : customerRedirectRoute}
                    />
                  );
                }}
              />

              <RestrictedRoute
                path="/practice"
                render={() =>
                  isFCA ? <PracticeDashboard /> : <PageNotFound />
                }
              />

              <RestrictedRoute path="/settings" component={SettingsDashboard} />

              <RestrictedRoute
                path="/docs/:groupId/:docId"
                component={ShareDocuments}
              />

              <RestrictedRoute
                path="/file-preview/:groupId/:docId"
                component={SharedFilePreview}
              />
              <RestrictedRoute exact path="/" render={() => <HomeRoute />} />

              <Route
                path="/home"
                render={(props) => {
                  const authCode = parse(props.location.search, {
                    ignoreQueryPrefix: true,
                  }).code;

                  if (authCode) {
                    return (
                      <Redirect to={`/settings${props.location.search}`} />
                    );
                  }
                  return <Redirect to={`/${props.location.search}`} />;
                }}
              />

              <Route exact path="/dashboard">
                <Redirect to={`/tax`} />
              </Route>
              <Route exact path="/intragroup">
                <Redirect to={`/books/intragroup/transfers/`} />
              </Route>
              <Route exact path="/intragroup/entities">
                <Redirect to="/tax/entities" />
              </Route>
              <Route exact path="/intragroup/transfers/">
                <Redirect to={`/books/intragroup/transfers/`} />
              </Route>
              <RestrictedRoute
                path={`${path}/intragroup/services/addtask/:templateId`}
                render={({ match }) => {
                  return (
                    <Redirect
                      to={`/books/intragroup/services/addtask${match.params.templateId}`}
                    />
                  );
                }}
              />
              <RestrictedRoute
                path={`${path}/intragroup/services/:params*`}
                render={({ match }) => {
                  return (
                    <Redirect
                      to={`/books/intragroup/services/${match.params.params}`}
                    />
                  );
                }}
              />
              <RestrictedRoute
                path={`${path}/books/intragroup/transfers/:params*`}
                render={({ match }) => {
                  return (
                    <Redirect
                      to={`/books/intragroup/transfers/${match.params.params}`}
                    />
                  );
                }}
              />
              <Route path={`/intragroup/transfers/transfer`}>
                <Redirect to={`/books/intragroup/transfers/transfer`} />
              </Route>

              <Route path="/intragroup/services">
                <Redirect to={`/books/intragroup/services`} />
              </Route>
              <Route exact path="/rewards/referral">
                <Redirect to="/billing/credits" />
              </Route>
              <Route exact path="/rewards">
                <Redirect to="/billing/credits" />
              </Route>
              <Route exact path="/tasks/addtask">
                <Redirect to="/filings/addtask" />
              </Route>
              <Route exact path="/tasks">
                <Redirect to="/filings" />
              </Route>
              <Route exact path="/chat/tickets">
                <Redirect to={isCustomer ? "/tickets" : "/admin/tickets"} />
              </Route>
              {isCustomer && (
                <Route exact path="/tickets">
                  <Redirect to="/settings/tickets" />
                </Route>
              )}
              <Route
                exact
                path="/tasks/:taskId"
                render={({
                  match: {
                    params: { taskId },
                  },
                }) => <Redirect to={`/filings/${taskId}`} />}
              />

              <Route
                exact
                path="/tasks/addtask/:templateId"
                render={({
                  match: {
                    params: { templateId },
                  },
                }) => <Redirect to={`/filings/addtask/${templateId}`} />}
              />

              <Route
                path="/board/captable"
                render={(props) => {
                  const entityId = parse(props.location.search, {
                    ignoreQueryPrefix: true,
                  }).entity;

                  if (entityId) {
                    return (
                      <Redirect
                        to={`/tax/entities/entity/${entityId}/cap-table`}
                      />
                    );
                  }
                  return <PageNotFound />;
                }}
              />

              <Route exact path="/interco">
                <Redirect to="/intragroup" />
              </Route>
              <Route exact path="/handbook">
                <Redirect to="/community/handbook" />
              </Route>
              <Route exact path="/bookkeeping">
                <Redirect to="/books" />
              </Route>
              <Route exact path="/bookkeeping/tp-payments">
                <Redirect to="/books/transfers" />
              </Route>
              <Route exact path="/mailbox">
                <Redirect to="/mailroom" />
              </Route>
              <Route exact path="/messages">
                <Redirect to="/chat" />
              </Route>
              <Route exact path="/adp-tax-credits">
                <AdpTaxCreditsRoute />
              </Route>
              <Route path="/complete-with-ai" exact>
                <CompleteWithAI />
              </Route>
              <AppRedirects from="/chat" to={isCpa ? "/admin" : "/tax"} />
              <AppRedirects
                from="/tickets"
                to={isCustomer ? "/tax" : "/admin"}
              />
              <AppRedirects from="/documents" to={isCpa ? "/admin" : "/tax"} />
              <AppRedirects from="/entities" to="/tax" />
              <AppRedirects from="/billing" to="/settings" />
              <AppRedirects from="/perks" to="/community" />
              <AppRedirects from="/filings" to="/tax" />
              <AppRedirects from="/deadlines" to="/tax" />
              <AppRedirects from="/compliance" to="/tax" />
              <AppRedirects from="/crm/:groupId" to="/admin" />
              <AppRedirects from="/crm" to="/admin" />

              <Route path="*">
                <PageNotFound />
              </Route>
            </Switch>
          </Suspense>
        </GlobalContexts>
      </Switch>
    </>
  );
};

const Routes = () => {
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(true);
  useUserOnboardingStepper();

  useEffect(() => {
    let recaptchaScript = document.getElementById("recaptcha-script");

    if (!recaptchaScript) {
      const scriptEl = document.createElement("script");
      scriptEl.id = "recaptcha-script";
      scriptEl.src = `https://www.google.com/recaptcha/api.js?render=${process.env.PUBLIC_RECAPTCHA_KEY}`;

      document.head.appendChild(scriptEl);
      recaptchaScript = scriptEl;
    }

    const onScriptLoad = () => {
      setRecaptchaLoaded(true);
    };
    recaptchaScript.addEventListener("load", onScriptLoad);

    return () => recaptchaScript?.removeEventListener("load", onScriptLoad);
  }, []);

  return (
    <BrowserRouter>
      <KeepInAppLinksInSameTab />
      <AuthProvider>
        <CurrentAppContextProvider>
          <CurrentGroupContextProvider>
            <Switch>
              <Route path="/vendor-bill/:billId" exact>
                <PublicVendorBill />
              </Route>
              <Route path="/vendor-bill-approve/:billId" exact>
                <ApprovePublicBill />
              </Route>
              <Route path="/mcp-confirm" exact>
                <MCPConfirmPage />
              </Route>
              <Route path="/setup" exact>
                <InvitationContext.Provider value={false}>
                  <MagicLink recaptchaLoaded={recaptchaLoaded} />
                </InvitationContext.Provider>
              </Route>
              <Route path="/invitation" exact>
                <InvitationContext.Provider value={true}>
                  <V2Signup />
                </InvitationContext.Provider>
              </Route>
              <Route path="/signin" exact>
                <InvitationContext.Provider value={false}>
                  <MagicLink recaptchaLoaded={recaptchaLoaded} />
                </InvitationContext.Provider>
              </Route>
              <Route path="/invitation/otpverify" exact>
                <InvitationContext.Provider value={true}>
                  <VerifyOtpPage />
                </InvitationContext.Provider>
              </Route>
              <Route path="/otpverify" exact>
                <InvitationContext.Provider value={false}>
                  <VerifyOtpPage />
                </InvitationContext.Provider>
              </Route>
              <Route path="/signup" exact>
                <SignupRoute />
              </Route>
              <Route path="/v2/signup" exact>
                <V2Signup />
              </Route>
              <Route path="/v2/filings-detected" exact>
                <FilingsDetected />
              </Route>
              <Route path="/v2/filings-dashboard" exact>
                <FilingsDashboard />
              </Route>
              <Route path="/v2/multi-year-setup" exact>
                <MultiYearSetup />
              </Route>
              <Route path="/notsupported" exact>
                <NotSupported />
              </Route>
              <Route path="/phoneverify" exact>
                <InvitationContext.Provider value={false}>
                  <PhoneVerify />
                </InvitationContext.Provider>
              </Route>
              <Route path="/invitation/phoneverify" exact>
                <InvitationContext.Provider value={true}>
                  <PhoneVerify />
                </InvitationContext.Provider>
              </Route>
              <Route path="/forgotPassword" exact component={ForgotPassword} />
              <Route path="/inkledeadlines" exact component={PublicCalendar} />
              <Route path="/inklecalendar" exact>
                <Redirect to="/inkledeadlines" />
              </Route>

              <Route
                path="/inklecalendar/:sessionId/:calendarType"
                exact
                component={PublicCalendar}
              />
              <Route
                exact
                path="/contractor-form/:merchantId"
                component={ContractorFormSelector}
              />
              <Route
                exact
                path="/contractor-form/:merchantId/form"
                component={ContractorForm}
              />
              <Route
                path="/invoice/:token"
                exact
                component={PublicInvoiceView}
              />
              <Route path="/form-7004-extension-request-2022" exact>
                <Redirect to="/" />
              </Route>
              <Route component={IconPage} path="/icons" />
              <Route component={ColorGrid} path="/colors" />
              <ProtectedRoutes path="/" component={AllProtectedRoutes} />
            </Switch>
          </CurrentGroupContextProvider>
        </CurrentAppContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
